<template>
    <div>
        <div class="row">
            <div class="col-md-3 mw-230" v-if="isHierarchyAllowed">
                <nested-list-component
                    :changeQuery="changeQuery"
                    :type="_.get(content, 'type')"
                    :timestamp="timestamp"
                    :endpoint="_.get(content, 'links.new')" />
            </div>
            <div class="col">
            <!-- custom top toolbar component -->
            <div v-if="inlineFormComponent">
                <component
                    v-on:input="addItemForm"
                    v-on:input::batch="addItems"
                    :loading="loading"
                    :type="_.get(content, 'schema.instanceType')"
                    :endpoint="_.get(content, 'links.new')"
                    :schema="_.get(content, 'schema.attributes')"
                    :items="_.get(content, 'data')"
                    :parent="parent"
                    :config="config"
                    :is="inlineFormComponent" />
            </div>

            <!-- default top toolbar -->
            <div v-if="!_.get(config, 'toolbarDisabled')">
                <b-navbar toggleable="md" class="filter-container" variant="">
                    <!-- left aligned nav items -->
                    <b-navbar-nav>
                        <b-nav-form v-if="_.get(config, 'searchable')" v-on:submit="changeSearch">
                            <b-form-input size="sm" class="mr-sm-2" type="text" v-model="search" placeholder="Keyword Search"/>
                            <button v-on:click="changeSearch" class="btn btn-navy btn-search mr-sm-2"><i class="material-icons">search</i></button>
                        </b-nav-form>
                    </b-navbar-nav>

                    <b-navbar-nav v-if="_.get(config, 'searchable') || hasFilters">
                        <button v-on:click="resetFilters" type="button" class="btn btn-blue-light btn-reset mr-sm-2">
                            <i class="material-icons">replay</i></button>
                    </b-navbar-nav>

                    <b-navbar-nav v-if="hasFilters">
                            <button v-on:click="toggleFilters" type="button" :class="[showFilters ? 'btn-show-filter' : 'btn-hide-filter']">
                               {{ showFilters ? 'Hide Filters' : 'Show Filters' }}</button>
                    </b-navbar-nav>

                </b-navbar>
            </div>

            <div v-if="hasFilters && showFilters">
                <b-navbar toggleable="md" class="filter-container filter-panel">
                    <b-navbar-nav class="flex-wrap">
                        <component
                            v-for="filter in _.get(config, 'customFilters', [])"
                            v-model="filter.value"
                            :key="filter.name"
                            :changeFilter="changeFilter"
                            :addFilter="addFilter"
                            :removeFilter="removeFilter"
                            :config="filter"
                            :is="filter.component" />
                        <div v-for="field in filterable" :key="field.name">
                            <label class="col-form-label pt-0">{{ getFilteringLabel(field.name) }}</label>
                            <b-nav-item-dropdown
                                :text="getFilteringText(field.name)"
                                extra-toggle-classes="navbar-filter"
                                extra-menu-classes="w-100"
                                left>
                                <b-dropdown-item
                                    v-on:click="changeFilter(field.name, null)">-- All --</b-dropdown-item>
                                <b-dropdown-item :key="value.id"
                                    v-for="value in field.values"
                                    v-on:click="changeFilter(field.name, value.id)"
                                    >{{__(value.name, ['titleCase'])}}</b-dropdown-item>
                            </b-nav-item-dropdown>
                        </div>
                    </b-navbar-nav>
                </b-navbar>
            </div>

            <!-- table pagination section -->
            <div v-if="pagination && _.get(config, 'pagination')" class="pagination-wrap">
                <div class="row">
                    <div class="col">
                        <b-pagination
                            v-if="pagination.last_page > 1"
                            v-on:change="changePage"
                            v-model="pagination.currentPage"
                            align="left"
                            size="md"
                            :limit="6"
                            :total-rows="pagination.total"
                            :per-page="pagination.perPage"
                            ></b-pagination>
                    </div>
                     <div class="col text-center">
						 <p class="pagination-count inline-block">Showing {{pagination.from}} to {{pagination.to}} of {{pagination.total}} {{__(content.type, ['titleCase'])}}</p>
                    </div>
                    <div class="col text-right">
                            <p class="inline-block">Rows per page</p>
                            <b-form-select class="rows-select" :options="pagination.options" cols=1 v-model="perPage" ></b-form-select>
                    </div>
                </div>
            </div>


            <template v-if="isEmpty">
                <table v-if="!_.get(config, 'hideIfEmpty')" class="table b-table table-pages " style="margin-bottom: 0;">
                    <thead>
                        <tr>
                            <th v-if="isDraggable" class="table-drag"></th>
                            <th v-if="_.size(_.get(config, 'batchActions'))" class="table-check">
                                <div class="checkbox checkbox-only">
                                    <input type="checkbox">
                                    <label v-on:click="toggleSelectAll" for="table-check-head"></label>
                                </div>
                            </th>
                            <template v-for="field in fields">
                                <th v-if="sortable(field)"
                                    v-on:click="changeSort(field)"
                                    :key="field"
                                    tabindex="0" aria-colindex="1">
                                    {{ getFieldLabel(field) || __(field, ['titleCase']) }}
                                </th>
                                <th v-else :key="field"
                                    tabindex="0" aria-colindex="1">
                                    {{ getFieldLabel(field) || __(field, ['titleCase']) }}
                                </th>
                            </template>
                            <th class="text-center">Actions</th>
                        </tr>
                    </thead>
                </table>
                <div v-if="!_.get(config, 'hideIfEmpty')" class="row" style="margin: 0;">
                    <div class="col" style="padding: 0;">
                        <div class="no-content text-center">
                            <img src="/img/sad-face.svg" alt="Sad">
                            <h3 v-if="_.get(config, 'emptyMessage')">{{ config.emptyMessage }}</h3>
                            <h3 v-else>You haven't created any {{__(content.type, ['lowerCase', 'plural'])}} yet</h3>
                            <template v-if="creatable">
                                <p>You can create your first {{__(content.type, ['singular'])}}<br/>using  the button below</p>
                                <button v-on:click="onCreating(content.type, content.schema)"
                                    class="btn btn-lg btn-success btn-rounded">
                                    <i class="material-icons">add</i> Create {{__(content.type, ['titleCase', 'singular'])}}</button>
                            </template>
                        </div>
                    </div>
                </div>
            </template>
            <div v-else  :class="[isHierarchyAllowed ? 'table-responsive-md' : '']">
                <table class="table b-table table-sorting table-pages">
                    <thead>
                        <tr>
                            <th v-if="isDraggable" class="table-drag"></th>
                            <th v-if="_.get(config, 'batchActions.length', 0)" class="table-check">
                                <div class="checkbox checkbox-only">
                                    <input type="checkbox">
                                    <label v-on:click="toggleSelectAll" for="table-check-head"></label>
                                </div>
                            </th>
                            <template v-for="field in fields">
                                <th v-if="sortable(field)"
                                    v-on:click="changeSort(field)"
                                    :key="field"
                                    :class="['sorting', sort[field] ? 'sorting_' + sort[field] : '']"
                                    tabindex="0" aria-colindex="1">
                                    {{ getFieldLabel(field) || __(field, ['titleCase']) }}
                                </th>
                                <th v-else :key="field"
                                    tabindex="0" aria-colindex="1">
                                    {{ getFieldLabel(field) || __(field, ['titleCase']) }}
                                </th>
                            </template>
                            <th v-if="_.get(config, 'actions', []).length > 0" class="text-center table-actions" style="width: 130px;">Actions</th>
                        </tr>
                    </thead>
                    <draggable
                        v-model="items"
                        v-on:end="changePriority"
                        :options="{ 'disabled': !isDraggable, handle: '.table-drag' }"
                        element="tbody">
                        <template v-for="item in items">
                            <tr :key="content.type + item.id" :class="[rowClass(item)]">
                                <td v-if="isDraggable" class="table-drag"><i class="material-icons">view_headline</i></td>
                                <td v-if="_.get(config, 'batchActions.length', 0)" class="table-check">
                                    <div class="checkbox checkbox-only">
                                        <input type="checkbox" :checked="selected.indexOf(item.id) >= 0" />
                                        <label v-on:click="toggleSelect(item.id)" for="tbl-check-1"></label>
                                    </div>
                                </td>
                                <td v-for="field in fields" :key="field">
                                    <component
                                        v-if="getFieldComponent(field, item)"
                                        v-on:change="(newValue) => updateItem(item.id, field, newValue, item)"
                                        :key="field"
                                        :initialValue="item[field]"
                                        :type="getFieldType(field)"
                                        :schema="getFieldSchema(field)"
                                        :data="item"
                                        :config="config.fields[field]"
                                        :references="_.get(content, 'schema.refs')"
                                        :is="getFieldComponent(field, item)"
                                        ></component>
                                    <template v-else>{{ item[field] }}</template>
                                </td>
                                <td v-if="_.get(config, 'actions', []).length > 0" class="table-actions text-right">
                                    <!-- <b-button :disabled="isPriorityDisabled" v-on:click="increasePriority(item.id)" variant="outline-success">&uarr;</b-button> -->
                                    <!-- <b-button :disabled="isPriorityDisabled" v-on:click="decreasePriority(item.id)" variant="outline-success">&darr;</b-button> -->
                                    <div class="btn-group" role="group">
                                        <template v-for="action in config.actions" v-if="action.primary">
                                            <button v-if="action.component == 'view-button'" :key="action.name"
                                                type="button" v-on:click="changeUrl(item.link)"
                                                class="btn btn-sm btn-secondary-outline">
                                                <i class="material-icons">visibility</i>
                                            </button>
                                            <button v-if="action.component == 'edit-button'" :key="action.name"
                                                type="button" v-on:click="changeUrl(item.link)"
                                                class="btn btn-sm btn-secondary-outline">
                                                <i class="material-icons">edit</i>
                                            </button>
                                            <button v-if="action.component == 'delete-button'" :key="action.name"
                                                type="button" v-on:click="deleteItem(item.id, item)"
                                                class="btn btn-sm btn-secondary-outline">
                                                <i class="material-icons">delete_outline</i>
                                            </button>
                                            <button v-if="action.component == 'preview-button'" :key="action.name"
                                                type="button" v-on:click="openPreview(item)"
                                                class="btn btn-sm btn-secondary-outline">
                                                <i class="material-icons">remove_red_eye</i>
                                            </button>
                                            <component
                                                v-if="_.isObject(action.component)"
                                                :key="action.name"
                                                :content="item"
                                                :config="action"
                                                :updateBatch="updateBatch"
                                                :addNotification="addNotification"
                                                :is="action.component">
                                            </component>
                                        </template>
                                        <div class="table-actions-compact" v-if="_.some(config.actions, action => !action.primary)">
                                            <button type="button" class="btn btn-round btn-secondary-outline" data-toggle="dropdown">
                                                <i class="material-icons">more_horiz</i>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-right text-left">
                                                <!-- <div class="btn-group-vertical" role="group"> -->
                                                <ul class="list-unstyled mb-0">
                                                <template v-for="action in config.actions" v-if="!action.primary">
                                                    <li :key="action.name">
                                                    <a href="javascript:void(0);" v-if="action.component == 'view-button'" :key="action.name"
                                                        v-on:click="changeUrl(item.link)"
                                                        class="dropdown-item">View</a>
                                                    <a href="javascript:void(0);" v-if="action.component == 'edit-button'" :key="action.name"
                                                        v-on:click="changeUrl(item.link)"
                                                        class="dropdown-item">Edit</a>
                                                    <a href="javascript:void(0);" v-if="action.component == 'preview-button'" :key="action.name"
                                                        v-on:click="openPreview(item)"
                                                        class="dropdown-item">Preview</a>
                                                    <a href="javascript:void(0);"
                                                        v-if="action.component == 'delete-button'"
                                                        :key="action.name"
                                                        v-on:click="deleteItem(item.id, item)"
                                                        class="dropdown-item">Delete</a>
                                                    <a href="javascript:void(0);" v-if="action.component == 'link-button'" :key="action.name"
                                                        v-on:click="changeUrl(item.link + '/' + action.path)"
                                                        class="dropdown-item"
                                                        >{{ action.name }}</a>
                                                    <component
                                                        v-if="_.isObject(action.component)"
                                                        :key="action.name"
                                                        :content="item"
                                                        :config="action"
                                                        :updateBatch="updateBatch"
                                                        :addNotification="addNotification"
                                                        :is="action.component">
                                                    </component>
                                                    </li>
                                                </template>
                                                </ul>
                                                <!-- </div> -->
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Table actions in small screen -->
                                </td>
                            </tr>
                        </template>
                    </draggable>
                </table>
            </div>

            <!-- table bottom pagination -->
            <div v-if="pagination && _.get(config, 'pagination')" class="pagination-wrap">
                <div class="row">
                    <div class="col">
                        <b-pagination
                            v-if="pagination.last_page > 1"
                            v-on:change="changePage"
                            v-model="pagination.currentPage"
                            align="left"
                            size="md"
                            :limit="6"
                            :total-rows="pagination.total"
                            :per-page="pagination.perPage"
                            ></b-pagination>
                    </div>
                     <div class="col text-center">
						 <p class="pagination-count inline-block">Showing {{pagination.from}} to {{pagination.to}} of {{pagination.total}} {{__(content.type, ['titleCase'])}}</p>
                    </div>
                    <div class="col text-right">
                            <p class="inline-block">Rows per page</p>
                            <b-form-select class="rows-select" :options="pagination.options" cols=1 v-model="perPage" ></b-form-select>
                    </div>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script>
import V from 'voca';
import _ from 'lodash';
import Draggable from 'vuedraggable';
import { singular, plural } from 'pluralize';

import NestedListComponent from './table-elements/NestedListComponent';

const memory = {};

export default {
    data() {
        const { filter: filtering, sort: sorting, search: searching, per_page } = this.query;

        let filter = {};
        if (filtering) {
            filter = filtering.split(/[\,\;\|]/)
                .map(keyValuePair => keyValuePair.split(':'))
                .reduce((tmp, [key, value]) => _.assign({}, tmp, { [key]: value }), {});
        }

        let sort = {};
        if (sorting) {
            sort = sorting.split(/[\,\;\|]/)
                .map(keyValuePair => keyValuePair.split(':'))
                .reduce((tmp, [key, value]) => _.assign({}, tmp, { [key]: value }), {});
        }

        let cacheKey = _.chain(this.content)
            .get('links.self')
            .split('?')
            .first()
            .value();
        return {
            cacheKey,
            initialData: {},
            perPage: _.get(this.query, 'per_page', 15),

            showFilters: false,
            filter,
            sort,
            search: searching || '',

            showFormModal: false,
            isFiltering: false,
            isSorting: !!sorting,
            isSearching: false,

            items: this.getContentItems(),
            creatingItem: {},
            ..._.get(memory, `${cacheKey}.data`, {}),

            // local timestamp to reset filter
            localTimestamp: Date.now(),
            loading: false,
        }
    },

    props: [
        'parent', 'content', 'timestamp',
        'createData', 'updateBatch', 'deleteData',
        'changeUrl', 'changeQuery', 'query',
        'onCreating', 'onInitialized', 'onSelected',
        'addNotification', 'selected', 'config',
    ],

    components: {
        NestedListComponent,
        Draggable,
    },

    watch: {
        perPage() {
            this.changePage(1)
        },
        timestamp() {
            this.items = this.getContentItems();
            const itemIds = this.items.map(item => item.id);
            const selected = this.selected.filter(itemId => itemIds.indexOf(itemId) >= 0);
            this.onSelected(this.content.type, selected);
        },
        collectionType(newVal, oldVal) {
            if (newVal == oldVal) return;

            // reset filter and selected
            this.filter = {};
            this.onInitialized(this.content.type, 'table-component', this.config);
            this.onSelected(this.content.type, []);
        },
        filter() {
            this.$forceUpdate();
        },
        query(newQuery) {
            if (!_.get(memory, [this.cacheKey, 'queries'])) {
                return memory[this.cacheKey].queries = [newQuery];
            }
            memory[this.cacheKey].queries.push(newQuery);
        },
    },

    computed: {
        filterable() {
            const schema = _.get(this.content, 'schema', {});
            const filterable = _.get(this.config, 'filterable', []);

            return _.chain(schema)
                .get('attributes', [])
                .filter(attr => filterable ? filterable.indexOf(attr.name) >= 0 : true)
                .filter(attr => attr.type === 'enum' || attr.type === 'ref' || attr.type === 'tinyint')
                .filter(attr => {
                    if (attr.type !== 'ref') return true;
                    if (!this.parent) return true;
                    return attr.relationship !== this.parent.type;
                })
                .map(attr => {
                    if (attr.type == 'ref' && attr.self) {
                        return { ...attr, values: this.content.schema.refs };
                    }
                    if (attr.type == 'enum' && attr.values) {
                        const values = _.map(attr.values, value => {
                            if (_.isObject(value)) {
                                return value;
                            }

                            return { id: value, name: value }
                        })

                        return { ...attr, values }
                    }
                    if (attr.type == 'tinyint') {
                        return {
                            ...attr, values: [
                                { id: '0', name: 'No' },
                                { id: '1', name: 'Yes' },
                            ]
                        }
                    }
                    return attr;
                })
                .value();
        },

        rowClass(){
            const rowClass = _.get(this.config, 'rowClass', '');
            if (_.isFunction(rowClass)) {
                return rowClass;
            }

            return () => rowClass;
        },

        hasFilters() {
            return _.get(this.config, 'customFilters', []).length + this.filterable.length > 0;
        },

        pagination() {
            if (this.isEmpty) return;

            const { links, meta } = this.content;

            if (meta && meta.total) {
                return {
                    currentPage: meta.current_page,
                    perPage: parseInt(meta.per_page),
                    total: parseInt(meta.total),
                    from: parseInt(meta.from),
                    to: parseInt(meta.to),
                    last_page: parseInt(meta.last_page),
                    options: [
                        { text: 5, value: 5 },
                        { text: 10, value: 10 },
                        { text: 15, value: 15 },
                        { text: 25, value: 25 },
                        { text: 50, value: 50 },
                        { text: 100, value: 100 },
                    ]
                }
            }
        },

        isEmpty() {
            return _.isEmpty(this.content.data);
        },

        isHierarchyAllowed() {
            return _.get(this.config, 'hierarchy');
        },

        subCollection() {
            return _.get(this.config, 'subCollection');
        },

        isPriorityAllowed() {
            return !this.isEmpty && !!_.get(this.content, 'data[0].meta.priority');
        },

        isPriorityDisabled() {
            if (!this.isPriorityAllowed) return true;
            return this.isSorting;
        },

        isDraggable() {
            return !this.isPriorityDisabled && _.get(this.config, 'draggable', true);
        },

        type() {
            if (!this.isEmpty) {
                return _.get(this.content, 'data[0].type');
            }
			return null;
        },

        collectionType() {
            return this.content.type;
        },

        nonKeyAttributeSchema(excluded) {
            return _.chain(this.content)
                .get('schema.attributes', {})
                .values()
                .filter(schema => schema.keyType != 'PRI')
                .reduce((tmp, schema) => {
                    if (schema.type == 'ref' && schema.self) {
                        return _.assign({}, tmp, { [schema.name]: { ...schema, values: this.content.schema.refs } });
                    }
                    return _.assign({}, tmp, { [schema.name]: schema });
                }, {});
        },

        fields() {
            let fieldConfigs = _.get(this.config, 'fields', []);
            fieldConfigs = _.isArray(fieldConfigs) ? fieldConfigs : _.keys(fieldConfigs);
            let fields = _.chain(this.nonKeyAttributeSchema).keys().union(fieldConfigs).value();

            return fields
                .filter(field => fieldConfigs ? fieldConfigs.indexOf(field) >= 0 : true)
                .sort((field1, field2) => {
                    const index1 = 1 / fieldConfigs.indexOf(field1);
                    const index2 = 1 / fieldConfigs.indexOf(field2);
                    return index1 > index2 ? -1 : 1;
                });
        },

        creatable() {
            return _.get(this.config, 'creatable');
        },

        inlineFormComponent() {
            return _.get(this.config, 'inlineFormComponent', null);
        },

        modalFormComponent() {
            return _.get(this.config, 'modalFormComponent', null);
        },
    },

    mounted() {
        console.log('Component mounted.')
		this.onInitialized(this.content.type, 'table-component', this.config);

        if (!_.get(memory, this.cacheKey, null)) {
            return memory[this.cacheKey] = {};
        }

        const memoryQuery = _.get(memory, this.cacheKey + '.queries', []);
        if (memoryQuery.length > 0) {
            this.changeQuery(_.last(memoryQuery));
        }
    },

    destroyed() {
        this.onSelected(this.content.type, []);
        if (!memory[this.cacheKey]) return;
        if (_.isArray(memory[this.cacheKey].queries)) {
            memory[this.cacheKey].queries.pop();
        }
        memory[this.cacheKey].data = {
            filter: this.filter,
            search: this.search,
            sort: this.sort,

            perPage: this.perPage,
            showFilters: this.showFilters,
            isFiltering: this.isFiltering,
            isSorting: this.isSorting,
            isSearching: this.isSearching,
        }
    },

    methods: {
        sortable(field) {
            const { sortable } = this.config;
            return sortable ? sortable.indexOf(field) >= 0 : true;
        },

        changePage(page) {
            this.changeQuery({
                page,
                per_page: this.perPage,
            })
        },

        addFilter(key, value) {
            this.filter[key] = value;
        },

        removeFilter(key) {
            this.filter = _.omit(this.filter, [key]);
        },

        changeFilter(key = null, value = null) {
            const entry = (key && value) ? `${key}:${value}` : '';
            if (key) {
                this.filter[key] = value;
            }

            const filterQuery = _.toPairs(this.filter).reduce((tmp, [field, fieldValue]) => {
                if (field == key) {
                    return tmp;
                }
                if (fieldValue) {
                    tmp.push(`${field}:${fieldValue}`);
                }

                return tmp;
            }, [entry]).filter(Boolean).join(';');

            this.isFiltering = !!filterQuery;
            this.changeQuery({
                filter: filterQuery,
                page: 1,
            });
        },


        serializeQuery (queryObject) {
            return _.toPairs(queryObject)
                .reduce((tmp, keyValuePair) => {
                    const [key, value] = keyValuePair;

                    return [...tmp, value ? `${key}:${value}` : key]
                }, [])
                .join(';')
        },

        deserializeQuery (queryString) {
            return _.chain(queryString)
                .split(/[;]/)
                .filter(Boolean)
                .reduce((tmp, keyValueString) => {
                    const [key, value] = keyValueString.split(/[\=\:]/);
                    if (!key) return tmp;

                    return { ...tmp, [key]: value || null }
                }, {})
                .value();
        },

        toggleFilters() {
            this.showFilters = !this.showFilters;
        },

        resetFilters() {
            this.localTimestamp = Date.now();

            let resetQuery = {
                filter: {},
                sort: {},
                search: '',
                page: 1,
            }

            const resetHandler = _.get(this.config, 'resetHandler');
            if (_.isFunction(resetHandler)) {
                resetQuery = resetHandler({
                    filter: this.deserializeQuery(this.query.filter),
                    sort: this.deserializeQuery(this.query.sort),
                    search: _.clone(this.search),
                });
            } else if (_.isObject(resetHandler)) {
                resetQuery = resetHandler;
            }

            this.filter = resetQuery.filter;
            this.sort = resetQuery.sort;
            this.search = resetQuery.search;

            const type = _.get(this.content, 'type');
            this.changeQuery({
                filter: this.serializeQuery(resetQuery.filter),
                sort: this.serializeQuery(resetQuery.sort),
                [`search[${type}]`]: resetQuery.search,
                timestamp: this.localTimestamp,
            });

            // reset custom filter values
            // _.get(this.config, 'customFilters', []).forEach(filterConfig => filterConfig.value = null);
        },

        changeSearch(evt) {
            evt.preventDefault();

            this.isSearching = !!this.search;
            const type = _.get(this.content, 'type');

            if (type) {
                return this.changeQuery({
                    [`search[${type}]`]: this.search || null,
                    page: 1,
                });
            }

            return this.changeQuery({
                search: this.search || null,
                page: 1,
            });
        },

        changeSort(key) {
            const sorting = this.updateSortingOrder(key);

            this.isSorting = !!sorting;
            this.changeQuery({
                sort: sorting,
                page: 1,
            });
        },

        showCreateForm() {

        },

        deleteItem(itemId, item) {
            if (item.protected) {
                alert('You cannot delete this content');
                return;
            }

            const type = _.chain(this.content).get('type', 'content').split('.').first().replace(/[\-\_]/, ' ').singular().value();
            const status = confirm(`Are you sure to delete this ${type}?`);

            if (!status) {
                return;
            }
            return this.updateBatch([
                { id: itemId, $delete: true },
            ], {
                subCollection: item.subCollection,
                setNotification: true,
            })
        },

        decreasePriority(itemId) {
            const items = this.content.data;

            const changingItem = items.find(item => item.id == itemId);
            const affectedItem = items.filter(item => item.meta.priority > changingItem.meta.priority)[0]

            if (!affectedItem) return;

            this.updateBatch([
                { id: changingItem.id, $update: { priority: affectedItem.meta.priority } },
                { id: affectedItem.id, $update: { priority: changingItem.meta.priority } },
            ], {
                setNotification: true,
            });
        },

        increasePriority(itemId) {
            const items = this.content.data;

            const changingItem = items.find(item => item.id == itemId);
            const affectedItem = items.filter(item => item.meta.priority < changingItem.meta.priority).pop();

            if (!affectedItem) return;

            this.updateBatch([
                { id: changingItem.id, $update: { priority: affectedItem.meta.priority } },
                { id: affectedItem.id, $update: { priority: changingItem.meta.priority } },
            ], {
                setNotification: true,
            });
        },

        changePriority(event) {
            const priorityField = this.items[0].priority;
            const priorityOrder = this.items[0].priority_order;
            const priorityAlgo = this.getSortAlgo(priorityOrder);

            const { oldIndex, newIndex } = event;
            if (oldIndex === newIndex) return;
            const [lesserIndex, greaterIndex] = (oldIndex - newIndex) < 0 ? [oldIndex, newIndex] : [newIndex, oldIndex];

            const changedItems = this.items.slice(lesserIndex, greaterIndex + 1);
            const changedItemIds = changedItems.map(item => item.id);

            const orderedPriorities = changedItems.map(item => item[priorityField]).slice().sort(priorityAlgo);

            const updatedData = _.zip(changedItemIds, orderedPriorities)
                .map(([id, priority]) => ({ id, $update:{ [priorityField]: priority } }))

            this.updateBatch(updatedData, {
                setNotification: true,
            });
        },

        updateSortingOrder(key) {
            let sortingOrder = this.sort[key];

            switch(sortingOrder) {
                case 'asc': sortingOrder = 'desc'; break;
                case 'desc': sortingOrder = null; break;
                default: sortingOrder = 'asc'; break;
            }

            this.sort = _.assign({}, { [key]: sortingOrder });

            return sortingOrder ? `${key}:${sortingOrder}` : '';
        },

        getFilteringLabel(field) {
            let label = _.format(field, ['titleCase']);
            return `Filter by ${label}`;
        },

        getFilteringText(field) {
            const value = this.filter[field];

            if (value == '0') {
                return 'No';
            }

            if (value == '1') {
                return 'Yes';
            }

            if (value) {
                return _.format(value, ['titleCase']);
            }

            return '-- All --';
        },

        getFilteringLabelForRef(field, values) {
            const id = this.filter[field];
            let label = this.titleCase(field.split('_')[0]);

            if (!id) {
				return `Filter by ${label}`;
            }
            const value = values.find(item => {
                return item.id === parseInt(id);
            })
            label += `: ${value.name}`;
            return label;
        },

        getContentItems() {
            let items = _.get(this.content, 'data', []);

            if (this.config.subCollection) {
                items = _.flatMap(items, (item) => {
                    let subItems = _.get(item, ['included', this.config.subCollection, 'data'])
                        .map(item => {
                            item.meta.subCollection = {
                                parent_id: item.attributes.parent_id,
                                subCollection: this.config.subCollection,
                            }
                            return item;
                        })

                    return _.concat([item], subItems);
                })
            }

            let sorting = _.get(this.content, 'meta.sort');
			if (items.length == 0) return [];

            if (sorting) {
                // display results from server as is

            } else if (_.has(items, '[0].meta.priority')) {
                // sort results based on priority field

                const priorityField = items[0].meta.priority;
                const priorityOrder = items[0].meta.priority_order;
                const priorityAlgo = this.getSortAlgo(priorityOrder);

                items = items.slice(0).sort((item1, item2) => {
                    const p1 = item1.attributes[priorityField] || 0;
                    const p2 = item2.attributes[priorityField] || 0;

                    return priorityAlgo(p1, p2);
                })
            }

            items = items.map(item => {
                const id = this.getId(item);
                return _.assign({}, item.attributes, item.meta || {}, {
                    id, link: this.helpers('uriFormat')(_.get(item, 'links.self')),
                })
            })

            return items;
        },

        getFieldType(field) {
            const schema = _.get(this.content, 'schema.attributes', {});

            return schema[field] ? schema[field].type : null;
        },

        getFieldComponent(field, item = null) {
            const { fields } = this.config;
            if (fields && fields[field] && fields[field].component) {
                const component = fields[field].component;
                return _.isFunction(component) ? component(item) : component;
            }
            return null;
        },

        getFieldSchema(field) {
            const schema = _.get(this.content, 'schema.attributes', {});

            return schema[field] || {};
        },

        getFieldLabel(field) {
            const { fields } = this.config;
            return _.get(fields, `${field}.label`);
        },

        updateItem(itemId, field, value, item) {
            this.updateBatch([
                { id: itemId, $update: { [field]: value } },
            ], {
                subCollection: item.subCollection,
                setNotification: true
            });
        },

        addItemModal() {
            const { defaultItem } = this.config;
            this.createData({ ...(defaultItem || {}), ...this.creatingItem }, {
                setNotification: true,
                addToCollection: true,
            });
            this.creatingItem = {};
            this.showFormModal = false;
        },

        changeItem(data) {

        },

        addItemForm(data) {
            const { defaultItem } = this.config;
            this.loading = true;
            this.createData({ ...(defaultItem || {}), ...data }, {
                setNotification: true,
                addToCollection: true,
            }).then(() => {
                this.loading = false;
                this.$forceUpdate();
            });
        },

        addItems(data) {
            const { defaultItem } = this.config;
            Promise.all(data.map(item => this.createData(
                { ...(defaultItem || {}), ...item },
                { setNotification: true, addToCollection: true }
           )))
        },

        toggleSelectAll() {
            let selected;
            if (this.selected.length > 0) {
                selected = [];
            } else {
                selected = this.items.map(item => item.id);
            }
            return this.onSelected(this.content.type, selected);
        },

        toggleSelect(itemId) {
            let selected;
            if (this.selected.indexOf(itemId) >= 0 ) {
                selected = this.selected.filter(id => id != itemId);
            } else {
                selected = [...this.selected, itemId];
            }
            const itemIds = this.items.map(item => item.id);
            selected = selected.filter((selectedId) => itemIds.indexOf(selectedId) >= 0)
            return this.onSelected(this.content.type, selected);
        },

        openFormModal() {
            this.showFormModal = true;
        },

        openPreview(item) {
            const previewUrl = `/admin/_embed_?url=${item.preview}#/${this.content.type}/${item.id}`;
            window.open(previewUrl, '_blank');
        },

        closeFormModal() {
            this.showFormModal = false;
        },

        titleCase(val) {
            return V.titleCase(val).replace(/\_Id$/, '').split('_').join(' ');
        },

        shorten(val) {
            const stringVal = String(val || '');
            if (stringVal.length < 32) return stringVal;
            return String(val)
                .split(' ')
                .slice(0, 8)
                .concat(['...'])
                .join(' ');
        },

        getId(item) {
            const key = _.get(this.config, 'key', 'id');
            return _.get(item, key);
        },

        getSortAlgo(order) {
            const op = (order == 'desc') ? -1 : 1;

            return (p1, p2) => (parseInt(p1) - parseInt(p2)) * op;
        },
    }
}
</script>



// WEBPACK FOOTER //
// seventytwo/72cms-ui/src/components/TableComponent.vue